import {
  Component,
  AfterViewInit,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  EventEmitter,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import * as echarts from 'echarts';
import { BehaviouralAssessmentGraphData } from '../assesment-fba.interface';
import { BehaviourFunction } from '../../../../insights.enums';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { AccessibilityService } from '../../../../../../core/services/accessibility.service';
import { CommonModule } from '@angular/common';

function getCssVar(varName: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return value ? `rgb(${value})` : '';
}

@Component({
  selector: 'app-screener-results-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './screener-results-graph.component.html',
})
export class ScreenerResultsGraphComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @Output() chartImage = new EventEmitter<string>();
  chartInstance!: echarts.ECharts;
  private printChartInstance: echarts.ECharts | null = null;
  @Input() graphData: BehaviouralAssessmentGraphData[] = [];
  @Input() chartId = '';
  data: BehaviouralAssessmentGraphData[] = this.graphData;
  sortedData: { name: string; value: number }[] = [];
  private readonly a11yService = inject(AccessibilityService);
  private readonly destroy$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphData'] && this.chartInstance) {
      this.updateChart();
    }
  }

  ngAfterViewInit(): void {
    const chartDom = document.getElementById(this.chartId)!;
    if (chartDom) {
      chartDom.innerHTML = '';
    }
    this.initChart();
    this.initPrintChart();
    this.a11yService.contrastMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => this.updateChart(), 0);
        setTimeout(() => this.updatePrintChart(), 0);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    if (this.printChartInstance) {
      this.printChartInstance.dispose();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.chartInstance?.resize();
    this.printChartInstance?.resize();
  }

  private prepareChartData(): { name: string; value: number }[] {
    const data = [
      {
        name: BehaviourFunction.SENSORY,
        value: this.getScoreByFunctionName(BehaviourFunction.SENSORY) || 0,
      },
      {
        name: BehaviourFunction.ESCAPE_AVOIDANCE,
        value:
          this.getScoreByFunctionName(BehaviourFunction.ESCAPE_AVOIDANCE) || 0,
      },
      {
        name: BehaviourFunction.TANGIBLE,
        value: this.getScoreByFunctionName(BehaviourFunction.TANGIBLE) || 0,
      },
      {
        name: BehaviourFunction.GAINING_ATTENTION,
        value:
          this.getScoreByFunctionName(BehaviourFunction.GAINING_ATTENTION) || 0,
      },
    ];

    return data.sort((a, b) => b.value - a.value);
  }

  private initChart(): void {
    const chartDom = document.getElementById(this.chartId)!;
    this.chartInstance = echarts.init(chartDom);
    this.sortedData = this.prepareChartData();
    this.updateChart();
  }

  private initPrintChart(): void {
    const printDom = document.getElementById(`print-${this.chartId}`);
    if (!printDom) return;
    if (this.printChartInstance) {
      this.printChartInstance.dispose();
    }
    this.printChartInstance = echarts.init(printDom);
    this.updatePrintChart();
    this.printChartInstance.on('finished', () => {
      this.emitPrintChartAsImage();
    });
  }

  private updateChart(): void {
    if (!this.chartInstance) return;

    this.sortedData = this.prepareChartData();
    const option = this.getChartOptions(false);
    this.chartInstance.setOption(option);
    this.updatePrintChart();
  }

  private updatePrintChart(): void {
    if (!this.printChartInstance) return;

    this.sortedData = this.prepareChartData();
    const option = this.getChartOptions(true);
    this.printChartInstance.setOption(option);
  }
  /**
   * Returns chart options for normal or print mode.
   * @param isPrint If true, returns print-friendly options.
   */
  private getChartOptions(isPrint: boolean): echarts.EChartsOption {
    const categoryNames = this.sortedData.map(item => item.name);
    const colorVars = isPrint
      ? {
          text: '#222222',
          axis: '#cccccc',
          legend: '#222222',
          sensory: '#4B9CD3',
          escape: '#F59E42',
          tangible: '#34C759',
          attention: '#0057B8',
        }
      : {
          text: getCssVar('--gray-900'),
          axis: getCssVar('--gray-200'),
          legend: getCssVar('--gray-600'),
          sensory: getCssVar('--light-blue'),
          escape: getCssVar('--warning-500'),
          tangible: getCssVar('--success-400'),
          attention: getCssVar('--blue'),
        };
    return {
      title: {
        text: 'Screener Results',
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 700,
          color: colorVars.text,
        },
      },
      grid: { left: 45, right: 45, top: 50, bottom: 70, containLabel: true },
      legend: {
        top: 40,
        left: 'center',
        orient: 'horizontal',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontSize: 12,
          fontWeight: 400,
          color: colorVars.legend,
        },
        data: categoryNames,
      },
      xAxis: {
        type: 'category',
        data: categoryNames,
        axisLabel: {
          interval: 0,
          rotate: 30,
          fontSize: 12,
          color: colorVars.legend,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: colorVars.axis,
          },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Score',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: 500,
          color: colorVars.legend,
          padding: [0, 0, 10, 0],
        },
        min: 0,
        max: 12,
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: colorVars.axis,
          },
        },
        axisLabel: {
          show: true,
          formatter: '{value}',
          color: colorVars.legend,
          fontSize: 12,
          margin: 10,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: colorVars.axis,
          },
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (
          params: echarts.TooltipComponentFormatterCallbackParams
        ) => {
          const dataIndex = Array.isArray(params) ? params[0].dataIndex : 0;
          const name = categoryNames[dataIndex];
          const value = this.sortedData[dataIndex].value;
          return `${name}: ${value}`;
        },
      },
      series: [
        {
          name: 'Score',
          type: 'bar',
          barWidth: '40%',
          data: this.sortedData.map(item => item.value),
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: (params: CallbackDataParams) => {
              const colorMap: Record<string, string> = {
                [BehaviourFunction.SENSORY]: colorVars.sensory,
                [BehaviourFunction.ESCAPE_AVOIDANCE]: colorVars.escape,
                [BehaviourFunction.TANGIBLE]: colorVars.tangible,
                [BehaviourFunction.GAINING_ATTENTION]: colorVars.attention,
              };
              return (
                colorMap[categoryNames[params.dataIndex]] || colorVars.sensory
              );
            },
          },
          label: {
            show: false,
          },
        },
      ],
    };
  }

  private emitPrintChartAsImage() {
    if (!this.printChartInstance) return;
    const chartImageBase64 = this.printChartInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });
    this.chartImage.emit(chartImageBase64);
  }

  getScoreByFunctionName(name: string): number | undefined {
    const result = this.graphData.find(
      item => item.behaviourFunctionName === name
    );
    return result ? result.score : undefined;
  }
}
